import {
  Bell,
  Building2,
  Calendar,
  ClipboardList,
  LayoutDashboard,
  LogIn,
  Palette,
  Rocket,
  ScrollText,
  Settings,
  Shield,
  UserRound,
  Users,
} from "lucide-react";

/**
 * Product docs for the in-app Docs hub.
 * Guides are filtered by superuserOnly / agencyOnly / permission on the index and detail pages.
 */

export const DOCS_GUIDES = [
  {
    slug: "getting-started",
    title: "Getting started",
    summary:
      "How your agency workspace is organized, who can do what, and how care history is preserved.",
    icon: Rocket,
    relatedSlugs: [
      "signing-in",
      "schedule-visits",
      "clients",
      "caregivers",
      "care-plans",
      "appearance",
      "notifications",
      "team-members",
      "organization-settings",
      "audit-logs",
      "platform-users",
      "roles-permissions",
    ],
    sections: [
      {
        heading: "Your agency workspace",
        paragraphs: [
          "Everything you see belongs to your organization (agency). Clients, caregivers, visits, and care plans are never shared with other agencies.",
          "Sign in with the account your organization admin created for you. Your role decides which menus and actions appear.",
        ],
      },
      {
        heading: "First week checklist",
        paragraphs: [
          "Use this order when an agency is new or you are setting up your own account for the first time.",
        ],
        steps: [
          "Sign in, then open Account to set your name and password if you were given a temporary one.",
          "Open Appearance from your avatar menu if you want a different theme or font (optional).",
          "Confirm Organization settings: agency name, contacts, and especially timezone.",
          "Invite or verify Team Members and roles so the right people can edit Care data.",
          "Add Clients and Caregivers in the directory.",
          "Create care plan templates and assign plans to active clients.",
          "Build the weekly Schedule and open a visit workspace to practice check-in, tasks, vitals, and checkout.",
          "Check the header bell for notifications and Audit Logs if your role includes compliance access.",
        ],
      },
      {
        heading: "Menus and navigation",
        paragraphs: [
          "Use the Care menu for day-to-day work: schedule, clients, caregivers, and care plans.",
          "Organization settings cover team members and the agency profile. Appearance and Account live under your avatar menu at the bottom of the sidebar.",
          "Platform menus (Organizations, Users, Roles & Permissions) appear only for platform superusers.",
        ],
        tips: [
          "Press Ctrl+K (Windows) or Cmd+K (Mac) to jump to any page you can access.",
          "Open Docs anytime from the sidebar for step-by-step guides.",
          "The header bell shows in-app notifications when your role includes notifications.view.",
        ],
      },
      {
        heading: "Permissions",
        paragraphs: [
          "Access is controlled by permission keys on your role (for example, scheduling visits or editing clients).",
          "If a page is missing from the sidebar, ask an organization admin to grant the matching permission—or use Roles & Permissions if you are a platform superuser.",
          "Writes fail closed: you may be able to view a module without create or edit rights.",
        ],
      },
      {
        heading: "Care history over delete",
        paragraphs: [
          "Clients are discharged rather than permanently deleted so visit and care history stays intact for continuity and compliance.",
          "Visits end as completed or cancelled. Care plans move through active, paused, or completed. Caregivers can be marked inactive when they leave.",
        ],
      },
      {
        heading: "Dates and timezone",
        paragraphs: [
          "Scheduling and “today” follow your agency’s timezone in Organization settings—not the server’s clock.",
          "Confirm the timezone is correct before planning a busy week so visit days line up with local time.",
        ],
      },
      {
        heading: "Where to go next",
        paragraphs: [
          "Open the related guides below for full how-tos on each module. The Docs index lists only guides your role can access.",
        ],
      },
    ],
  },
  {
    slug: "signing-in",
    title: "Signing in",
    summary: "Log in with email and password, and what happens when a session ends.",
    icon: LogIn,
    sections: [
      {
        heading: "Sign in",
        paragraphs: [
          "Open the login page and enter the email and password for your agency (or platform) account.",
          "After a successful sign-in, agency staff land on the organization dashboard; platform superusers land on the platform dashboard.",
        ],
        steps: [
          "Go to the login page.",
          "Enter your email and password.",
          "Continue into your workspace.",
        ],
      },
      {
        heading: "Session ended",
        paragraphs: [
          "If your session expires while you are working, the app returns you to login with a message that the session ended so you can sign in again and continue.",
          "There is no API token stored in the browser for you to manage—authentication uses a secure session cookie.",
        ],
        tips: [
          "If you cannot sign in, ask an admin to confirm your account is Active and that you have the right email.",
          "Change your password anytime from Account after you are signed in.",
        ],
      },
    ],
  },
  {
    slug: "platform-dashboard",
    title: "Platform dashboard",
    summary: "Cross-tenant overview: organizations, users, and system activity.",
    icon: LayoutDashboard,
    to: "/platform",
    superuserOnly: true,
    sections: [
      {
        heading: "What you see",
        paragraphs: [
          "The platform dashboard summarizes agencies, users, and recent activity across the whole system—not a single organization.",
          "Use it as a launch pad into Organizations, Users, and Roles & Permissions.",
        ],
      },
      {
        heading: "Typical workflow",
        steps: [
          "Open Platform from the sidebar after signing in as a superuser.",
          "Review organization and user counts, including inactive or suspended agencies if shown.",
          "Register a new organization before provisioning its staff users.",
          "Adjust that agency’s role permission matrix so staff can open Care modules.",
        ],
      },
    ],
  },
  {
    slug: "organization-dashboard",
    title: "Organization dashboard",
    summary: "Agency overview with activity, staffing signals, and shortcuts.",
    icon: LayoutDashboard,
    to: "/",
    agencyOnly: true,
    sections: [
      {
        heading: "Home for your agency",
        paragraphs: [
          "The organization dashboard is the landing page after login for agency staff. It highlights recent activity and shortcuts into Care and Organization areas you can access.",
        ],
      },
      {
        heading: "Getting around",
        steps: [
          "Scan today’s signals for visits and staffing.",
          "Use shortcuts or the Care menu to open Schedule, Clients, or Caregivers.",
          "Open Docs if you need a how-to for a module you are using for the first time.",
        ],
      },
    ],
  },
  {
    slug: "schedule-visits",
    title: "Schedule & visits",
    summary:
      "Plan the week, assign caregivers, and document visit check-in and check-out.",
    icon: Calendar,
    to: "/schedule",
    permission: "scheduling.visits.view",
    agencyOnly: true,
    sections: [
      {
        heading: "Weekly schedule",
        paragraphs: [
          "Open Schedule from the Care menu to see visits across the week. Days follow your agency’s timezone. The grid covers typical daytime hours (about 6:00–22:00).",
        ],
        steps: [
          "Use Prev / Today / Next to change the week.",
          "Optionally filter the week by caregiver.",
          "Create a visit with + Schedule Visit, or click an empty slot.",
          "You need scheduling create or edit permission to add or change visits.",
        ],
      },
      {
        heading: "Create or edit a visit",
        paragraphs: [
          "The visit modal collects client (required), caregiver (optional—can stay Unassigned), title, service type, date, start/end time, status, and notes.",
          "You can copy tasks from an active client care plan when scheduling, and quick-add a client or caregiver if your role allows.",
        ],
        steps: [
          "Fill required fields and save.",
          "After create, the client on that visit is locked; change other fields or open the workspace as needed.",
          "Cancel a visit from the modal when it will not occur (you will be asked to confirm).",
        ],
        tips: [
          "Visit statuses include scheduled, confirmed, in progress, completed, and cancelled—each uses a distinct color on the week grid.",
          "Empty weeks show “No visits this week” above the hours grid.",
        ],
      },
      {
        heading: "Visit workspace",
        paragraphs: [
          "Open a visit’s workspace from the modal or schedule to check in and out, record vitals, complete care tasks, and write clinical notes.",
        ],
        steps: [
          "Start Visit (Check In) when care begins.",
          "Toggle or add care tasks; notes auto-save as you type.",
          "Enter vitals (temperature, blood pressure, pulse, SpO₂, weight) and Save Vitals.",
          "End Visit (Check Out) when finished—checkout finalizes notes so they cannot be edited further.",
        ],
        tips: [
          "Completed visits show that notes are finalized.",
          "A timeline on the workspace records key events for the visit.",
        ],
      },
      {
        heading: "Other views",
        paragraphs: [
          "From a caregiver’s profile, open View Schedule to see only that person’s week.",
          "On a client record, the Visits History tab lists past and upcoming visits for that client.",
        ],
      },
    ],
  },
  {
    slug: "care-plans",
    title: "Care plans",
    summary:
      "Build templates, assign plans to clients, and track task checklists.",
    icon: ClipboardList,
    to: "/care-plans",
    permission: "careplans.plans.view",
    agencyOnly: true,
    sections: [
      {
        heading: "Templates vs client plans",
        paragraphs: [
          "Templates are reusable checklists your agency maintains (name, category, default duration, description, active flag, and task list).",
          "Client care plans are assigned copies tied to a specific client—built from a template or created as a custom plan. Active plans can feed task lists when you schedule visits.",
        ],
      },
      {
        heading: "Manage templates",
        steps: [
          "Open Care Plans, then Manage Templates (or go to Care Plan Templates).",
          "Search or filter by category, then create or edit a template.",
          "Add the task checklist caregivers should follow.",
          "Archive a template when it should no longer be offered for new assignments (inactive templates show as archived).",
        ],
      },
      {
        heading: "Assign to a client",
        steps: [
          "From Client Care Plans, or the Care Plans tab on a client record, choose Assign Care Plan.",
          "Pick an active template, or enter a custom title and category with start (and optional end) dates.",
          "Active plans appear in the main list; Plan History shows paused or completed plans on the client tab.",
        ],
        tips: [
          "Categories include Personal Care, Medication, Mobility, Companionship, Nursing, and Custom.",
          "Only active templates appear in the assign picker.",
        ],
      },
    ],
  },
  {
    slug: "clients",
    title: "Clients",
    summary: "Maintain the client directory, status, and care history.",
    icon: UserRound,
    to: "/clients",
    permission: "directory.clients.view",
    agencyOnly: true,
    sections: [
      {
        heading: "Directory",
        paragraphs: [
          "Clients lists everyone your agency serves. Search and filter by status. Open a record to edit profile details and related care activity.",
        ],
        steps: [
          "Open Clients from the Care menu.",
          "Use New Client to add someone (requires create/write access).",
          "Fill personal, contact, and address fields, then Save.",
          "New clients default to Prospective until you move them to Active or another status.",
        ],
      },
      {
        heading: "Statuses",
        paragraphs: [
          "Client statuses include Prospective, Active, On hold, and Discharged.",
          "When care ends, set status to Discharged. That keeps the record and history—there is no Delete on clients.",
        ],
      },
      {
        heading: "Client record tabs",
        paragraphs: [
          "Existing clients show Profile, Care Plans, and Visits History tabs so you can manage plans and review visits without leaving the record.",
        ],
        tips: [
          "If the form is read-only, your role cannot edit directory records—ask an owner, admin, or coordinator.",
          "If Clients is missing from the sidebar, your role needs directory.clients.view.",
        ],
      },
    ],
  },
  {
    slug: "caregivers",
    title: "Caregivers",
    summary: "Manage caregiver profiles and open their individual schedules.",
    icon: Users,
    to: "/caregivers",
    permission: "directory.caregivers.view",
    agencyOnly: true,
    sections: [
      {
        heading: "Profiles",
        paragraphs: [
          "Caregivers are the field staff assigned to visits. Keep contact details and status up to date so scheduling stays accurate.",
        ],
        steps: [
          "Open Caregivers from the Care menu.",
          "Add or edit personal, contact, and address fields as needed.",
          "New caregivers default to Applicant; set Active when they are ready for assignments.",
          "Mark Inactive when they leave so they are not used for new visits.",
        ],
      },
      {
        heading: "Schedule and delete",
        paragraphs: [
          "On an existing caregiver, use View Schedule to open that person’s week.",
          "Unlike clients, caregivers may be permanently deleted when your role allows—confirm carefully; prefer Inactive when you only need to stop assignments.",
        ],
        tips: [
          "Empty lists encourage adding the first caregiver; filtered empties mean no one matches the current search or status.",
        ],
      },
    ],
  },
  {
    slug: "account",
    title: "Account",
    summary: "Update your profile details and change your password.",
    icon: UserRound,
    to: "/account",
    sections: [
      {
        heading: "Your profile",
        paragraphs: [
          "Account is available from your avatar menu in the sidebar. Update first name, last name, and phone. Email is read-only—ask an admin if it must change.",
        ],
        steps: [
          "Open Account from the user menu.",
          "Edit your details and save.",
          "Use the password section: current password, new password (at least 8 characters), and confirm.",
        ],
        tips: [
          "Changing your password keeps you signed in.",
          "Theme and font preferences are on Appearance, not Account.",
        ],
      },
    ],
  },
  {
    slug: "appearance",
    title: "Appearance",
    summary: "Choose your personal theme and font for the admin panel.",
    icon: Palette,
    to: "/settings/appearance",
    sections: [
      {
        heading: "Personal look and feel",
        paragraphs: [
          "Appearance is per user. It does not change how other staff see the app. Open it from your avatar menu (Account, Appearance, Sign out) or from the command palette.",
        ],
        steps: [
          "Pick a light or dark theme (for example Violet, Blue, Midnight, Ocean).",
          "Choose a font such as Inter, Source Sans 3, or IBM Plex.",
          "Preview live, then Save to store preferences on your profile—or Discard to revert.",
        ],
      },
    ],
  },
  {
    slug: "notifications",
    title: "Notifications",
    summary: "Read agency activity from the header bell and mark items read.",
    icon: Bell,
    permission: "notifications.view",
    sections: [
      {
        heading: "Header bell",
        paragraphs: [
          "Notifications live in the site header bell—not as a sidebar page. The menu appears when your role includes notifications.view (platform superusers always have access).",
        ],
        steps: [
          "Open the bell to load recent activity for your agency.",
          "Click an item to mark it read, or use Mark all read.",
          "Unread count shows on the icon (caps at 99+).",
        ],
        tips: [
          "Empty state: “No recent activity.”",
          "Each item shows a title, message, and time.",
        ],
      },
    ],
  },
  {
    slug: "team-members",
    title: "Team members",
    summary: "Invite staff, set roles, and control who can edit agency data.",
    icon: Users,
    to: "/settings/users",
    permission: "organization.users.view",
    agencyOnly: true,
    sections: [
      {
        heading: "Who can work in the agency",
        paragraphs: [
          "Team Members lists staff accounts for your organization: name, email, role, and Active/Inactive. Managing invites needs organization.users.manage.",
        ],
        steps: [
          "Open Team Members under Organization settings.",
          "Invite someone with email, name, and role (Owner, Administrator, Care coordinator, or Caregiver).",
          "Share the one-time temporary password returned after invite so they can sign in and change it.",
          "Deactivate accounts that should no longer access the agency; reactivate if they return.",
        ],
        tips: [
          "Default invite role is Care coordinator.",
          "Writes fail closed: without edit or create permission, users can view but not change Care data.",
          "Platform superusers provision users across agencies from Platform → Users and tune permissions under Roles & Permissions.",
        ],
      },
    ],
  },
  {
    slug: "organization-settings",
    title: "Organization settings",
    summary: "Agency name, timezone, and contact details.",
    icon: Settings,
    to: "/settings/org",
    permission: "organization.settings.view",
    agencyOnly: true,
    sections: [
      {
        heading: "Agency profile",
        paragraphs: [
          "Organization settings hold your agency’s display name, contacts, and timezone. Editing requires organization.settings.edit.",
        ],
        steps: [
          "Open Organization settings from the sidebar.",
          "Update name and contact email/phone as needed.",
          "Set the timezone that matches where care is delivered.",
        ],
      },
      {
        heading: "Why timezone matters",
        paragraphs: [
          "Visit days, “today,” and schedule views use the agency timezone. A wrong timezone shifts which calendar day a visit appears on.",
        ],
      },
    ],
  },
  {
    slug: "audit-logs",
    title: "Audit logs",
    summary: "Read-only history of sensitive actions for compliance review.",
    icon: ScrollText,
    to: "/audit-logs",
    permission: "audit.logs.view",
    sections: [
      {
        heading: "What is recorded",
        paragraphs: [
          "Audit Logs under Compliance list sensitive actions for review. Entries are read-only—you cannot edit or delete them from the app.",
        ],
        steps: [
          "Open Audit Logs (requires audit.logs.view).",
          "Search by actor, action, or description.",
          "Filter by category: auth, directory, scheduling, careplans, organization, or system.",
        ],
      },
      {
        heading: "What each row shows",
        paragraphs: [
          "Columns include timestamp, actor (name/email), action, category, description (with target when present), and IP address.",
          "Summary cards highlight total events, sign-ins today, data mutations, and active actors. Platform superusers also see an Organization column for cross-tenant review.",
        ],
      },
    ],
  },
  {
    slug: "organizations",
    title: "Organizations",
    summary: "Platform console for registering and managing agencies.",
    icon: Building2,
    to: "/admin/agencies",
    superuserOnly: true,
    sections: [
      {
        heading: "Agency lifecycle",
        paragraphs: [
          "Organizations is the platform console for creating and managing tenant agencies. Register an agency before adding its users and Care data.",
        ],
        steps: [
          "Open Organizations from the Platform menu.",
          "Create a new organization with name, timezone, and optional contacts. Status can be Active or Inactive.",
          "A URL slug is generated from the name automatically and shown in the list.",
          "Provision staff under Users, then set that agency’s permission matrix under Roles & Permissions.",
        ],
        tips: [
          "Inactive marks the agency without deleting it.",
          "Delete organization permanently removes the tenant—confirm only when that is intentional.",
        ],
      },
    ],
  },
  {
    slug: "platform-users",
    title: "Platform users",
    summary: "Provision staff across agencies or create platform superusers.",
    icon: Users,
    to: "/admin/users",
    superuserOnly: true,
    sections: [
      {
        heading: "Cross-tenant user list",
        paragraphs: [
          "Platform Users lists accounts across all organizations: name, email, role, organization (or Platform Admin), superuser flag, and Active/Inactive.",
        ],
        steps: [
          "Open Users from the Platform menu.",
          "Create a user with email and password (email cannot change later).",
          "For agency staff: leave Superuser off, pick an organization, and choose a role (Owner, Administrator, Care coordinator, or Caregiver).",
          "For platform admins: turn Superuser on—no organization is assigned.",
          "On edit, leave password blank to keep the current one; set Active/Inactive as needed.",
        ],
        tips: [
          "A user is either a superuser or tied to exactly one agency—not both.",
          "Agency day-to-day invites can also be done from Team Members inside that organization.",
          "Delete user permanently removes the account—prefer Inactive when access should stop temporarily.",
        ],
      },
    ],
  },
  {
    slug: "roles-permissions",
    title: "Roles & permissions",
    summary: "Configure which role can view or change each feature per agency.",
    icon: Shield,
    to: "/admin/roles",
    superuserOnly: true,
    sections: [
      {
        heading: "Per-agency matrix",
        paragraphs: [
          "Roles & Permissions lets you pick an organization and edit which permissions each role (Owner, Administrator, Care coordinator, Caregiver) receives.",
          "Rows are grouped by area—Directory, Scheduling, Care Plans, Organization, Audit, Notifications. Missing grants are denied.",
        ],
        steps: [
          "Open Roles & Permissions from the Platform menu.",
          "Select the organization to configure.",
          "Toggle permission cells for the roles that should have access.",
          "Save each role column when it has changes.",
        ],
        tips: [
          "Superusers bypass the matrix and always have full access.",
          "After changing grants, ask affected staff to refresh or sign in again if menus still look wrong.",
          "Organization permissions include managing team members and settings; notifications.view controls the header bell.",
        ],
      },
    ],
  },
];

export function getGuideBySlug(slug) {
  return DOCS_GUIDES.find((guide) => guide.slug === slug) || null;
}

/**
 * Whether the signed-in user may see this guide (same rules as the Docs index).
 */
export function canAccessGuide(guide, user, can) {
  if (!guide) return false;
  if (guide.superuserOnly && !user?.is_superuser) return false;
  if (guide.agencyOnly && user?.is_superuser) return false;
  if (guide.permission && !can(guide.permission)) return false;
  return true;
}

/**
 * Reference categories for the MDN-style docs index.
 * Order matches a typical agency workflow, then platform admin.
 */
export const DOC_CATEGORIES = [
  {
    id: "overview",
    title: "Overview",
    description:
      "How the workspace is organized, signing in, and the home dashboards.",
    slugs: [
      "getting-started",
      "signing-in",
      "organization-dashboard",
      "platform-dashboard",
    ],
  },
  {
    id: "care",
    title: "Care",
    description:
      "Scheduling visits, care plans, and documenting care in the field.",
    slugs: ["schedule-visits", "care-plans"],
  },
  {
    id: "directory",
    title: "Directory",
    description: "Client and caregiver records and their statuses.",
    slugs: ["clients", "caregivers"],
  },
  {
    id: "organization",
    title: "Organization",
    description:
      "Team members, agency profile, notifications, and compliance logs.",
    slugs: [
      "team-members",
      "organization-settings",
      "notifications",
      "audit-logs",
    ],
  },
  {
    id: "account",
    title: "Account & preferences",
    description: "Your profile, password, and personal appearance settings.",
    slugs: ["account", "appearance"],
  },
  {
    id: "platform",
    title: "Platform administration",
    description:
      "Cross-tenant organizations, users, and the roles permission matrix.",
    slugs: ["organizations", "platform-users", "roles-permissions"],
  },
];

/** Categories with only the guides the current user can open. */
export function listAccessibleCategories(user, can) {
  return DOC_CATEGORIES.map((category) => ({
    ...category,
    guides: category.slugs
      .map((slug) => getGuideBySlug(slug))
      .filter((guide) => canAccessGuide(guide, user, can)),
  })).filter((category) => category.guides.length > 0);
}
